import { makeAutoObservable, runInAction } from "mobx";
import agent from "../api/agent";
import { QuickDeliveryConfigurationApiResponse } from "../models/miscellaneous";
import { store } from "./store";

export interface QuickDeliveryConfigPayload {
  delivery_type: number;
  warehouseId: number;
}

export default class QuickDeliveryStore {
  quickDeliveryConfig: QuickDeliveryConfigurationApiResponse | null = null;
  loadingQuickDelivery: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  /*------------------ Quick Delivery Loader ------------------*/
  getLoadingQuickDelivery = () => this.loadingQuickDelivery;

  setLoadingQuickDelivery = (val: boolean) => {
    this.loadingQuickDelivery = val;
  };

  /*------------------ Quick Delivery Actions ------------------*/
  getQuickDeliveryConfiguration = async (
    payload: QuickDeliveryConfigPayload
  ) => {
    this.setLoadingQuickDelivery(true);
    try {
      const params = new URLSearchParams();
      params.append("delivery_type", String(payload.delivery_type));
      params.append("warehouseId", String(payload.warehouseId));

      const result = await agent.Cart.quickDeliveryConfiguration(params);
      runInAction(() => {
        this.quickDeliveryConfig = result;
      });
      return result;
    } catch (error) {
      console.error("Error fetching quick delivery configuration:", error);
      throw error;
    } finally {
      this.setLoadingQuickDelivery(false);
    }
  };

  /*------------------ Helper Methods ------------------*/

  // Check if quick delivery is enabled
  get isQuickDeliveryEnabled() {
    return this.quickDeliveryConfig?.data?.quickDeliveryStatus === 1;
  }

  // Get quick delivery OFF message
  get quickDeliveryOffMessage() {
    return this.quickDeliveryConfig?.data?.quickDeliveryOFFMessage || "";
  }

  // Get delivery time TAT
  get deliveryTimeTat() {
    return this.quickDeliveryConfig?.data?.delivery_time_tat || "";
  }

  // Get quick delivery details
  get quickDeliveryDetails() {
    return this.quickDeliveryConfig?.data?.quickDelivery || null;
  }

  // Check if quick delivery is within operational hours
  isWithinOperationalHours = () => {
    const quickDelivery = this.quickDeliveryDetails;
    if (!quickDelivery) return false;

    const now = new Date();
    const currentTime =
      now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

    // Parse start_time (format: "HH:MM:SS")
    const [startHour, startMin, startSec] = quickDelivery.start_time
      .split(":")
      .map(Number);
    const startTimeInSeconds = startHour * 3600 + startMin * 60 + startSec;

    // Parse end_time (format: "HH:MM:SS")
    const [endHour, endMin, endSec] = quickDelivery.end_time
      .split(":")
      .map(Number);
    const endTimeInSeconds = endHour * 3600 + endMin * 60 + endSec;

    return currentTime >= startTimeInSeconds && currentTime <= endTimeInSeconds;
  };

  // Clear quick delivery configuration
  clearQuickDeliveryConfig = () => {
    runInAction(() => {
      this.quickDeliveryConfig = null;
    });
  };

  // Validate quick delivery availability and show error if not available
  validateQuickDeliveryAvailability = (): boolean => {
    if (!this.isQuickDeliveryEnabled) {
      const message =
        this.quickDeliveryOffMessage ||
        "Quick Delivery is currently unavailable. Please try again later.";
      store.toasterStore.error(message);
      return false;
    }
    return true;
  };

  // Check and validate quick delivery with operational hours
  validateQuickDeliveryWithHours = (): boolean => {
    if (!this.isQuickDeliveryEnabled) {
      const message =
        this.quickDeliveryOffMessage ||
        "Quick Delivery is currently unavailable. Please try again later.";
      store.toasterStore.error(message);
      return false;
    }

    if (!this.isWithinOperationalHours()) {
      const quickDelivery = this.quickDeliveryDetails;
      const message = quickDelivery
        ? `Quick Delivery is only available between ${quickDelivery.start_time} and ${quickDelivery.end_time}.`
        : "Quick Delivery is not available at this time.";
      store.toasterStore.error(message);
      return false;
    }

    return true;
  };
}
